import type { BoardSample, ChapterBoard } from "../../board";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { MARKER_DIAMETER, markerDiscAxisOffsetY } from "../MarkerMetrics/metrics";
import {
  POV_INDICATOR_GAP_PX,
  povCharacterAnchor,
  povCharacterIdAtPlayhead,
} from "./anchor";
import { markerRailPose } from "../../rail/RailPose/pose";
import { type PovSmoothState, easedPovAnchor } from "./smooth";

const BADGE_GAP_PX = 2;
const NAME_GAP_PX = 8;

let smoothState: PovSmoothState | null = null;
let lastFrameMs = 0;

/** The POV portrait sits in front, so the badge is never over someone else's face. */
function bringPovDiscForward(root: HTMLElement, povId: string): void {
  const disc = root.querySelector<HTMLElement>(
    `[data-marker-part="disc"][data-character-id="${povId}"]`,
  );
  if (disc !== null) {
    disc.style.zIndex = "2";
  }
}

export function applyPovIndicator(
  root: HTMLElement,
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
  board?: ChapterBoard,
  sample?: BoardSample,
): void {
  const el = root.querySelector<HTMLElement>("[data-pov-indicator]");
  if (el === null) {
    return;
  }
  const anchor = povCharacterAnchor(
    layout,
    playheadMs,
    viewportWidth,
    board,
    sample,
  );
  const povId = povCharacterIdAtPlayhead(layout, playheadMs);
  if (anchor === null || povId === undefined) {
    el.style.visibility = "hidden";
    el.style.opacity = "0";
    smoothState = null;
    el.classList.remove("timeline-pov-indicator--hop");
    return;
  }
  const switching =
    smoothState !== null && smoothState.povId !== povId;
  const previousPovId = smoothState?.povId;
  const nowMs = performance.now();
  const eased = easedPovAnchor(
    smoothState,
    anchor,
    povId,
    lastFrameMs === 0 ? undefined : nowMs - lastFrameMs,
  );
  lastFrameMs = nowMs;
  smoothState = eased.state;
  if (switching) {
    el.classList.remove("timeline-pov-indicator--hop");
    void el.offsetWidth;
    el.classList.add("timeline-pov-indicator--hop");
  }
  bringPovDiscForward(root, povId);
  if (switching && previousPovId !== undefined) {
    const previous = root.querySelector<HTMLElement>(
      `[data-marker-part="disc"][data-character-id="${previousPovId}"]`,
    );
    if (previous !== null) {
      previous.style.zIndex = "1";
    }
  }
  el.style.visibility = "visible";
  // A past self fades where its rail meets the present one, and its badge goes with it.
  el.style.opacity = String(markerRailPose(povId, playheadMs, layout.rail)?.fade ?? 1);
  // Just under the name below the disc, however many lines it takes.
  const name = root.querySelector<HTMLElement>(
    `[data-marker-part="name"][data-character-id="${povId}"]`,
  );
  const belowName = eased.anchor.topY + POV_INDICATOR_GAP_PX + MARKER_DIAMETER
    + markerDiscAxisOffsetY() + NAME_GAP_PX + (name?.offsetHeight ?? 0) + BADGE_GAP_PX;
  el.style.transform =
    `translate(${eased.anchor.anchorX}px, ${belowName}px) translate(-50%, 0)`;
}
